import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useAction } from "convex/react";
import { api } from "convex/_generated/api";
import { samplePuzzle, createPuzzleRecord } from "../lib/samplePuzzles";
import { convertNYTToGuardian } from "../lib/converters/nytToGuardian";
import type { NYTPuzzle } from "../lib/converters/nytToGuardian";

export function AdminPage() {
  const [status, setStatus] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [startId, setStartId] = useState<string>("17365");
  const [endId, setEndId] = useState<string>("17360");
  const hasAccess = useQuery(api.admin.checkAccess);
  const puzzles = useQuery(api.puzzles.list, {});
  const seedPuzzles = useMutation(api.puzzles.seed);
  const createPuzzle = useMutation(api.puzzles.create);
  const fetchMultipleCrosswords = useAction(api.guardianFetch.fetchMultipleCrosswords);
  const fetchCrossword = useAction(api.guardianFetch.fetchCrossword);

  // Show loading state
  if (hasAccess === undefined) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Show access denied if user is not admin
  if (hasAccess === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-red-600 text-5xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Access Denied
          </h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the admin panel. Please contact
            an administrator if you need access.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  const handleSeedSample = async () => {
    setStatus("Seeding sample puzzle...");
    try {
      const record = createPuzzleRecord(samplePuzzle);
      await seedPuzzles({ puzzles: [record] });
      setStatus("Sample puzzle seeded successfully!");
    } catch (error) {
      setStatus(`Error: ${error}`);
    }
  };

  const handleFetchGuardianCrosswords = async (count: number = 5) => {
    setIsLoading(true);
    setStatus(`Fetching ${count} recent Guardian Quick crosswords...`);
    try {
      // Build list of crosswords to fetch (recent Quick crosswords)
      const latestId = 17365; // As of January 2026
      const crosswordsToFetch = [];
      for (let i = 0; i < count; i++) {
        crosswordsToFetch.push({
          type: "quick",
          id: latestId - i,
        });
      }

      const puzzles = await fetchMultipleCrosswords({ crosswords: crosswordsToFetch });
      setStatus(`Successfully fetched ${puzzles.length} crosswords. Seeding to database...`);

      const records = puzzles.map(createPuzzleRecord);
      await seedPuzzles({ puzzles: records });

      setStatus(`Successfully seeded ${puzzles.length} Guardian crosswords!`);
    } catch (error) {
      setStatus(`Error fetching Guardian crosswords: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchGuardianRange = async () => {
    const start = parseInt(startId);
    const end = parseInt(endId);

    if (isNaN(start) || isNaN(end)) {
      setStatus("Invalid start or end ID");
      return;
    }

    if (start < end) {
      setStatus("Start ID must be greater than or equal to End ID");
      return;
    }

    const count = start - end + 1;
    setIsLoading(true);
    setStatus(`Fetching ${count} Guardian Quick crosswords (${end} to ${start})...`);

    try {
      const crosswordsToFetch = [];
      for (let id = start; id >= end; id--) {
        crosswordsToFetch.push({
          type: "quick",
          id: id,
        });
      }

      const puzzles = await fetchMultipleCrosswords({ crosswords: crosswordsToFetch });
      setStatus(`Successfully fetched ${puzzles.length} crosswords. Seeding to database...`);

      const records = puzzles.map(createPuzzleRecord);
      await seedPuzzles({ puzzles: records });

      setStatus(`Successfully seeded ${puzzles.length} Guardian crosswords!`);
    } catch (error) {
      setStatus(`Error fetching Guardian crosswords: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFetchSpecificCrossword = async () => {
    const type = prompt("Enter crossword type (quick, cryptic, prize, etc.):");
    if (!type) return;

    const idStr = prompt("Enter crossword ID:");
    if (!idStr) return;

    const id = parseInt(idStr);
    if (isNaN(id)) {
      setStatus("Invalid ID");
      return;
    }

    setIsLoading(true);
    setStatus(`Fetching Guardian ${type} crossword #${id}...`);
    try {
      const puzzle = await fetchCrossword({ type, id });
      setStatus(`Successfully fetched crossword. Seeding to database...`);

      const record = createPuzzleRecord(puzzle);
      await seedPuzzles({ puzzles: [record] });

      setStatus(`Successfully seeded Guardian ${type} #${id}!`);
    } catch (error) {
      setStatus(`Error fetching crossword: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportNYT = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setStatus("Importing NYT puzzle...");
    try {
      const text = await file.text();
      const nytData: NYTPuzzle = JSON.parse(text);
      const guardianData = convertNYTToGuardian(nytData);

      await createPuzzle({
        sourceId: guardianData.id,
        source: "nyt",
        title: guardianData.name,
        author: guardianData.creator?.name,
        date: guardianData.date,
        dimensions: guardianData.dimensions || { rows: 15, cols: 15 },
        data: guardianData,
      });

      setStatus("NYT puzzle imported successfully!");
      event.target.value = ""; // Reset file input
    } catch (error) {
      setStatus(`Error: ${error}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold">Admin - Puzzle Management</h1>
          <a
            href="/"
            className="text-blue-600 hover:underline text-sm"
          >
            Back to Home
          </a>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Fetch Guardian Crosswords</h2>
          <p className="text-sm text-gray-600 mb-3">
            Fetch real Guardian crosswords directly from their website
          </p>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => handleFetchGuardianCrosswords(3)}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
            >
              Fetch 3 Recent Quick Crosswords
            </button>
            <button
              onClick={() => handleFetchGuardianCrosswords(10)}
              disabled={isLoading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
            >
              Fetch 10 Recent Quick Crosswords
            </button>
          </div>

          <div className="border-t pt-4 mb-4">
            <h3 className="text-sm font-semibold mb-3">Fetch Range of Quick Crosswords</h3>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">Start ID</label>
                <input
                  type="number"
                  value={startId}
                  onChange={(e) => setStartId(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="17365"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">End ID</label>
                <input
                  type="number"
                  value={endId}
                  onChange={(e) => setEndId(e.target.value)}
                  disabled={isLoading}
                  className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  placeholder="17360"
                />
              </div>
              <button
                onClick={handleFetchGuardianRange}
                disabled={isLoading}
                className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                Fetch Range
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Enter a range of IDs to fetch (e.g., 17365 to 17360 will fetch 6 crosswords)
            </p>
          </div>

          <button
            onClick={handleFetchSpecificCrossword}
            disabled={isLoading}
            className="px-4 py-2 bg-green-700 text-white rounded hover:bg-green-800 disabled:bg-gray-400 text-sm"
          >
            Fetch Specific Crossword (by type & ID)
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Seed Sample Puzzles</h2>
          <button
            onClick={handleSeedSample}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Seed Sample Puzzle
          </button>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Import NYT Puzzle JSON</h2>
          <p className="text-sm text-gray-600 mb-3">
            Upload a JSON file from the NYT Crosswords archive
          </p>
          <input
            type="file"
            accept=".json"
            onChange={handleImportNYT}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
        </div>

        {status && (
          <div className="bg-gray-100 rounded p-4 mb-6">
            <pre className="text-sm whitespace-pre-wrap">{status}</pre>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">
            Existing Puzzles ({puzzles?.length || 0})
          </h2>
          {puzzles?.length === 0 ? (
            <p className="text-gray-500 text-sm">No puzzles yet. Seed some above!</p>
          ) : (
            <div className="space-y-2">
              {puzzles?.map((puzzle) => (
                <div key={puzzle._id} className="border-b py-2 last:border-b-0">
                  <div className="font-medium">{puzzle.title}</div>
                  <div className="text-sm text-gray-500">
                    {puzzle.source} - {puzzle.dimensions.cols}×{puzzle.dimensions.rows}
                    {puzzle.author && ` - ${puzzle.author}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
